import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { resource } from '@util/resource';

export type AdditionalQuestionAnswerType =
  | 'single_choice'
  | 'multiple_choice'
  | 'free_text';

export interface AdditionalQuestionOptionInput {
  id?: number;
  sortOrder?: number;
  texts: Record<string, string>;
}

export interface UpsertAdditionalQuestionRequest {
  id?: string;
  answerType: AdditionalQuestionAnswerType;
  texts: Record<string, string>;
  options?: AdditionalQuestionOptionInput[];
}

export interface AdditionalQuestionListItem {
  id: string;
  answerType: AdditionalQuestionAnswerType;
  texts: Record<string, string>;
  displayText: string;
  hasAnswers: boolean;
}

export interface AdditionalQuestionDetail {
  id: string;
  answerType: AdditionalQuestionAnswerType;
  texts: Record<string, string>;
  options: AdditionalQuestionOptionDetail[];
  hasAnswers: boolean;
  typeEditable: boolean;
}

export interface AdditionalQuestionOptionDetail {
  id: number;
  sortOrder: number;
  texts: Record<string, string>;
}

export interface ExperienceAdditionalQuestionAssignment {
  questionId: string;
  sortOrder: number;
  required: boolean;
}

export interface ExperienceAdditionalQuestionView {
  questionId: string;
  answerType: AdditionalQuestionAnswerType;
  texts: Record<string, string>;
  options: AdditionalQuestionOptionDetail[];
  sortOrder: number;
  required: boolean;
}

export interface ReservationAdditionalQuestionAnswer {
  questionId: string;
  answerType: AdditionalQuestionAnswerType;
  questionTextSnapshot: Record<string, string>;
  answerSnapshot: Record<string, unknown>;
}

export interface ReservationAdditionalQuestionExportPayload {
  headers: string[];
  answersByReservationId: Record<string, Record<string, string>>;
}

@Injectable({ providedIn: 'root' })
export class AdditionalQuestionsService {
  list(search?: string): Observable<AdditionalQuestionListItem[]> {
    return resource('crm://v2/additional-questions')
      .params({ search: search || undefined })
      .get<AdditionalQuestionListItem[]>();
  }

  get(id: string): Observable<AdditionalQuestionDetail> {
    return resource('crm://v2/additional-questions/id')
      .params({ id })
      .get<AdditionalQuestionDetail>();
  }

  upsert(request: UpsertAdditionalQuestionRequest): Observable<{ id: string }> {
    return resource('crm://v2/additional-questions').post(request);
  }

  delete(id: string): Observable<void> {
    return resource('crm://v2/additional-questions/id')
      .params({ id })
      .delete();
  }

  hasAnswers(id: string): Observable<{ hasAnswers: boolean }> {
    return resource('crm://v2/additional-questions/id/has-answers')
      .params({ id })
      .get<{ hasAnswers: boolean }>();
  }

  listExperienceAssignments(
    experienceId: string
  ): Observable<ExperienceAdditionalQuestionView[]> {
    return resource('crm://v2/experiences/experienceId/additional-questions')
      .params({ experienceId })
      .get<ExperienceAdditionalQuestionView[]>();
  }

  listReservationAnswers(
    reservationId: string
  ): Observable<ReservationAdditionalQuestionAnswer[]> {
    return resource('crm://v2/reservations/reservationId/additional-question-answers')
      .params({ reservationId })
      .get<ReservationAdditionalQuestionAnswer[]>();
  }

  exportForPeriod(
    wineryId: string,
    start: string,
    end: string,
    lang?: string
  ): Observable<ReservationAdditionalQuestionExportPayload> {
    return resource('crm://v2/reservations/additional-question-answers/export')
      .params({
        wineryId,
        'dateRange.startOfRange': start,
        'dateRange.endOfRange': end,
        lang: lang || undefined,
      })
      .get<ReservationAdditionalQuestionExportPayload>();
  }

  exportForReservations(
    wineryId: string,
    reservationIds: string[],
    lang?: string
  ): Observable<ReservationAdditionalQuestionExportPayload> {
    if (!reservationIds.length) {
      return of({ headers: [], answersByReservationId: {} });
    }
    return resource('crm://v2/reservations/additional-question-answers/export').post({
      wineryId,
      reservationIds,
      lang: lang || undefined,
    });
  }
}
